import {
  BUSINESS_TIMEZONE,
  doPeriodsOfTheDayOverlap,
  type PeriodOfTheDay,
  type PeriodOfTheDayWithoutAllday,
} from '@omecare/utils';
import { formatInTimeZone } from 'date-fns-tz';

export type ManualReviewItem = {
  invoiceIdentifier: string;
  beneficiary: string;
  date: string;
  totalAmountDue: number;
  totalAmountPaid: number;
  slotsThatDay: Array<{
    id: string;
    periodOfTheDay: PeriodOfTheDay;
    startAt: string;
    endAt: string;
    doctorSubstituteId: string | null;
  }>;
  attendingHasWorkScheduleThatDay: boolean;
};

export type HalfDayChoice = PeriodOfTheDayWithoutAllday;
export type ManualReviewState = Record<string, HalfDayChoice | undefined>;

export function buildManualReviewState(
  items: ManualReviewItem[],
): ManualReviewState {
  const state: ManualReviewState = {};
  for (const item of items) {
    state[item.invoiceIdentifier] = undefined;
  }
  return state;
}

export function isSubmitDisabled(state: ManualReviewState): boolean {
  return Object.values(state).some((v) => v === undefined);
}

export type WarningKind = 'work_schedule_fallback' | 'invoice_will_be_lost';

// Appointments DB sync runs at 12:30 and 23:30 Europe/Paris. Outside that
// window the half-day data may not yet reflect the latest sync.
const SYNC_WINDOW_START_MINUTES = 12 * 60 + 30;
const SYNC_WINDOW_END_MINUTES = 23 * 60 + 30;

export function isOutsideSyncWindow(now: Date): boolean {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  const minutesOfDay = hour * 60 + minute;
  return (
    minutesOfDay < SYNC_WINDOW_START_MINUTES ||
    minutesOfDay > SYNC_WINDOW_END_MINUTES
  );
}

export function computeWarning(
  item: ManualReviewItem,
  choice: HalfDayChoice | undefined,
): WarningKind | null {
  if (!choice) return null;
  const hasMatchingSlot = item.slotsThatDay.some((s) =>
    doPeriodsOfTheDayOverlap({ a: choice, b: s.periodOfTheDay }),
  );
  if (hasMatchingSlot) return null;
  return item.attendingHasWorkScheduleThatDay
    ? 'work_schedule_fallback'
    : 'invoice_will_be_lost';
}

/**
 * Slot pointer carried by the accounting doctolib-data errors, as consumed by
 * the review table to flag the rows the operator has to fix.
 */
export type MissingDoctolibSlot = {
  id: string;
  startAt: Date | string;
  periodOfTheDay: string;
};

/**
 * Local (browser timezone) calendar day of a slot, as `yyyy-MM-dd`.
 *
 * Review rows carry a plain day string while error slots carry a full instant;
 * both are compared on the day the operator sees in the app.
 *
 * @param startAt - the slot start instant
 * @returns the calendar day, `yyyy-MM-dd`
 */
function toCalendarDay(startAt: Date | string): string {
  return formatInTimeZone(new Date(startAt), BUSINESS_TIMEZONE, 'yyyy-MM-dd');
}

/**
 * Resolves which review rows are impacted by the slots the API reported as
 * missing their doctolib data.
 *
 * A row matches when it references the offending slot directly (same slot id in
 * `slotsThatDay`), or, failing that, when it falls on the same calendar day —
 * the offending slot may not be listed on any row when the day never reached
 * manual review.
 *
 * @param params.items - the review rows currently displayed
 * @param params.slots - the offending slots reported by the API
 * @returns the invoice identifiers of the impacted rows
 */
export function findInvoicesImpactedBySlots({
  items,
  slots,
}: {
  items: ManualReviewItem[];
  slots: MissingDoctolibSlot[];
}): Set<string> {
  const slotIds = new Set(slots.map((slot) => slot.id));
  const slotDays = new Set(slots.map((slot) => toCalendarDay(slot.startAt)));
  const impacted = new Set<string>();
  for (const item of items) {
    const referencesSlot = item.slotsThatDay.some((slot) =>
      slotIds.has(slot.id),
    );
    if (referencesSlot || slotDays.has(item.date)) {
      impacted.add(item.invoiceIdentifier);
    }
  }
  return impacted;
}
